
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartHandshake, Lock, MessageCircle, Users, Home, LogIn } from 'lucide-react';

const Footer = () => {
  const footerLinks = [
    { to: '/', label: 'Home', icon: <Home size={20} /> },
    { to: '/buddys', label: "Onze Buddy's", icon: <Users size={20} /> },
    { to: '/veiligheid', label: 'Veiligheid & Privacy', icon: <Lock size={20} /> },
    { to: '/contact', label: 'Neem Contact Op', icon: <MessageCircle size={20} /> },
  ];

  const fadeIn = (delay = 0) => ({
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: delay } },
  });

  return (
    <footer className="bg-cm-blue-dark text-cm-blue-light/80 pt-16 pb-8 mt-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-tr from-cm-pink/20 via-transparent to-cm-yellow/20 opacity-30"></div>
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <motion.div variants={fadeIn(0)} initial="hidden" animate="visible">
            <h3 className="font-sans font-medium text-2xl text-white mb-4">Classbuddy</h3>
            <p className="text-sm mb-4 leading-relaxed text-white">
              Een veilige en vriendelijke plek waar ieder kind kan praten, leren en groeien met een eigen AI-buddy.
            </p>
            <div className="flex items-center text-cm-yellow">
              <HeartHandshake size={22} className="mr-2" />
              <span className="font-semibold">Met zorg & liefde gemaakt</span>
            </div>
          </motion.div>
          
          <motion.div variants={fadeIn(0.1)} initial="hidden" animate="visible">
            <h4 className="font-sans font-medium text-xl text-white mb-4">Ontdek Meer</h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="hover:text-cm-yellow transition-colors duration-300 flex items-center text-sm group"
                  >
                    {React.cloneElement(link.icon, { className: 'mr-3 text-cm-pink group-hover:text-cm-yellow transition-colors' })}
                    <span className="font-medium">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={fadeIn(0.2)} initial="hidden" animate="visible">
            <h4 className="font-sans font-medium text-xl text-white mb-4">Ondersteuning</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/faq" className="hover:text-cm-yellow transition-colors duration-300 text-sm font-medium text-white">
                  Veelgestelde Vragen (FAQ)
                </Link>
              </li>
              <li>
                <Link to="/voor-scholen" className="hover:text-cm-yellow transition-colors duration-300 text-sm font-medium text-white">
                  Informatie voor Scholen
                </Link>
              </li>
              <li>
                <Link to="/login" className="hover:text-cm-yellow transition-colors duration-300 flex items-center text-sm group text-white">
                  <LogIn size={20} className="mr-3 text-cm-pink group-hover:text-cm-yellow transition-colors" />
                  <span className="font-medium">Inloggen Portaal</span>
                </Link>
              </li>
            </ul>
          </motion.div>
          
          <motion.div variants={fadeIn(0.3)} initial="hidden" animate="visible">
            <h4 className="font-sans font-medium text-xl text-white mb-4">Contactgegevens</h4>
            {/* E-mailadres */}
            <p className="text-sm mb-2 text-white">
              Email:{" "}
              <a
                href="mailto:info@classbuddy.nl"
                className="hover:text-cm-yellow font-medium text-white"
              >
                info@classbuddy.nl
              </a>
            </p>
            {/* Telefoon */}
            <p className="text-sm mb-2 text-white">
              Telefoon:{" "}
              <a
                href="tel:0627165899"
                className="hover:text-cm-yellow font-medium text-white"
              >
                06-27165899
              </a>
            </p>
            {/* Adres */}
            <p className="text-sm font-medium text-white">
              Adres: Keurenplein 41, 1069CD Amsterdam
            </p>
            {/* Google Maps-knop */}
            <p className="mt-2">
              <a
                href="https://www.google.com/maps?q=Keurenplein+41,+1069CD+Amsterdam"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-cm-yellow transition-colors text-sm text-white inline-flex items-center gap-2"
              >
                <span role="img" aria-label="locatie">📍</span> Route naar ons
              </a>
            </p>
          </motion.div>
        </div>
        
        <motion.div
          variants={fadeIn(0.4)}
          initial="hidden"
          animate="visible"
          className="text-center text-xs border-t border-cm-blue/30 pt-8 text-white"
        >
          <p>&copy; {new Date().getFullYear()} Classbuddy AI. Alle rechten voorbehouden. Gemaakt met ❤️ in Nederland.</p>
          <p className="mt-2">
            <Link to="/privacybeleid" className="hover:text-cm-yellow underline text-white">Privacybeleid</Link> | <Link to="/gebruikersvoorwaarden" className="hover:text-cm-yellow underline text-white">Gebruiksvoorwaarden</Link>
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
